import React, { type CSSProperties } from 'react';
import { ActionIcon, Anchor, Tooltip } from '@mantine/core';
import { IconExternalLink, IconPlayerPlay, IconVolume, IconVolumeOff } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Annotation } from '@/types/Annotation.model';
import { IProduct } from '@/types/Product.model';
import { AnnotationProductOpenButton } from './AnnotationProductOpenButton';
import { useSpeechSynthesis } from '@/hooks/useSpeechSynthesis';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import { isYouTubeUrl, getYouTubeVideoId, isSafeHttpUrl, isSafeAnnotationImageSrc } from '@/utils/annotationUtils';
import classes from './InfoBox.module.css';

interface AnnotationInfoBoxProps {
  activeAnnotation: Annotation;
  annotationPosition: { x: number; y: number };
  descriptionBoxRef: React.RefObject<HTMLDivElement>;
  /** Accent color for highlights (gradient bar, chips, hover states). */
  backgroundColor?: string;
  onProductClick?: (product: IProduct) => void;
  onImageClick?: () => void;
}

function resolveAccentColor(color?: string): string {
  if (!color || color === '#000000' || color === '#000') {
    return DEFAULT_QR_CODE_COLOR;
  }
  return color;
}

/**
 * Floating annotation detail card shown beside the clicked hotspot.
 */
export function AnnotationInfoBox({
  activeAnnotation,
  annotationPosition,
  descriptionBoxRef,
  backgroundColor = DEFAULT_QR_CODE_COLOR,
  onProductClick,
  onImageClick,
}: AnnotationInfoBoxProps) {
  const { t } = useTranslation();
  const accentColor = resolveAccentColor(backgroundColor);

  const rootStyle = {
    left: `${annotationPosition.x + 20}px`,
    top: `${annotationPosition.y}px`,
    '--annotation-accent': accentColor,
  } as CSSProperties;

  return (
    <div
      ref={descriptionBoxRef}
      className={classes.root}
      style={rootStyle}
      data-annotation-info-box
    >
      <div className={classes.accentBar} aria-hidden />

      <div className={classes.header}>
        <h4 className={classes.title}>{activeAnnotation.title}</h4>
        {activeAnnotation.productId && onProductClick ? (
          <div className={classes.headerActions}>
            <AnnotationProductOpenButton
              productId={activeAnnotation.productId}
              onOpen={onProductClick}
              className={classes.productButton}
            />
          </div>
        ) : null}
      </div>

      <div className={classes.body}>
        {activeAnnotation.type === 'animation' && activeAnnotation.animationTrack ? (
          <span className={classes.animationBadge}>
            <IconPlayerPlay size={11} stroke={2.25} />
            {t('editProduct.playingAnimation')}: {activeAnnotation.animationTrack}
          </span>
        ) : null}

        {activeAnnotation.text?.trim() ? (
          <AnnotationDescription text={activeAnnotation.text} />
        ) : null}

        {activeAnnotation.link &&
        isSafeHttpUrl(activeAnnotation.link) &&
        isYouTubeUrl(activeAnnotation.link) ? (
          <YouTubeEmbed url={activeAnnotation.link} />
        ) : activeAnnotation.link && isSafeHttpUrl(activeAnnotation.link) ? (
          <LinkWithTooltip href={activeAnnotation.link} text={t('editProduct.viewProduct')} />
        ) : null}

        {activeAnnotation.image &&
        isSafeAnnotationImageSrc(activeAnnotation.image) &&
        onImageClick ? (
          <div
            className={classes.imageThumb}
            onClick={onImageClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onImageClick();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={activeAnnotation.title}
          >
            <img src={activeAnnotation.image} alt={activeAnnotation.title} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function AnnotationDescription({ text }: { text: string }) {
  const { t, i18n } = useTranslation();
  const { supported, speaking, toggle } = useSpeechSynthesis(text, i18n.language);
  const speakLabel = speaking ? t('editProduct.stopSpeaking') : t('editProduct.speakDescription');

  return (
    <div className={classes.textRow}>
      <p className={classes.text}>{text}</p>
      {supported ? (
        <Tooltip label={speakLabel} withArrow position="top">
          <ActionIcon
            className={`${classes.speakButton}${speaking ? ` ${classes.speakButtonActive}` : ''}`}
            size="sm"
            variant="subtle"
            aria-label={speakLabel}
            aria-pressed={speaking}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              toggle();
            }}
          >
            {speaking ? <IconVolumeOff size={15} stroke={2} /> : <IconVolume size={15} stroke={2} />}
          </ActionIcon>
        </Tooltip>
      ) : null}
    </div>
  );
}

function YouTubeEmbed({ url }: { url: string }) {
  const { t } = useTranslation();
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return null;

  return (
    <div className={classes.youtubeBlock}>
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title={t('editProduct.youtubeVideoTitle')}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        className={classes.youtubeFrame}
      />
      <Anchor href={url} target="_blank" rel="noopener noreferrer" className={classes.youtubeLink}>
        {t('editProduct.openInYouTube')}
        <IconExternalLink size={11} />
      </Anchor>
    </div>
  );
}

function LinkWithTooltip({ href, text }: { href: string; text: string }) {
  const getDomainFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace(/^www\./, '');
    } catch {
      const match = url.match(/https?:\/\/(?:www\.)?([^/]+)/);
      return match ? match[1] : url;
    }
  };

  const domain = getDomainFromUrl(href);

  return (
    <div className={classes.linkWrap}>
      <Anchor href={href} target="_blank" rel="noopener noreferrer" className={classes.linkPill}>
        {text}
        <IconExternalLink size={12} />
      </Anchor>
      <span className={classes.linkTooltip}>{domain}</span>
    </div>
  );
}
